import { Link, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import {
  AlertCircle,
  ArrowRight,
  LoaderCircle,
  Pencil,
  Plus,
  Settings2,
  Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Modal } from "@/components/ui/modal";
import { useMe } from "@/lib/auth";
import { type Obra, useCreateObra, useDeleteObra, useObras, useUpdateObra } from "@/lib/obras";
import { ObraForm, type ObraFormValues } from "./ObraForm";

// toInput normaliza los valores del formulario al payload del API (descripción vacía ⇒ null).
function toInput(values: ObraFormValues) {
  const descripcion = values.descripcion.trim();
  return {
    nombre: values.nombre.trim(),
    descripcion: descripcion === "" ? null : descripcion,
    activa: values.activa,
  };
}

/**
 * GestionObrasPage es la administración de la colección de obras (F7 #161), competencia
 * exclusiva del ADMIN global: listado con su código correlativo y estado, alta/edición en
 * modal y baja con confirmación (soft-delete). Cada obra enlaza a su configuración
 * (`/obras/$obraId/configuracion`); la pertenencia de cada persona se gestiona en su ficha,
 * en `/usuarios` (#238). El backend es el guardián real (RequireAdmin sobre la colección);
 * la ruta ya redirige a quien no es admin.
 */
export function GestionObrasPage() {
  const { data: me } = useMe();
  const esAdmin = me?.es_admin ?? false;

  // El estado vacío del selector `/obras` entra aquí con `?nueva` para abrir el alta de una
  // (flujo de primer arranque, #187): la lectura inicial de useState lo consume una sola vez.
  const search = useSearch({ strict: false }) as { nueva?: boolean };

  const { data: obras, isPending, isError } = useObras();
  const crear = useCreateObra();
  const editar = useUpdateObra();
  const borrar = useDeleteObra();

  const [formState, setFormState] = useState<
    { mode: "nueva" } | { mode: "editar"; obra: Obra } | null
  >(search.nueva ? { mode: "nueva" } : null);
  const [aBorrar, setABorrar] = useState<Obra | null>(null);
  // Tras crear una obra se ofrece un paso guiado para añadirle miembros de inmediato (#187).
  const [obraCreada, setObraCreada] = useState<Obra | null>(null);

  const columns = useMemo<ColumnDef<Obra, unknown>[]>(
    () => [
      {
        accessorKey: "codigo_obra",
        header: "Código",
        cell: ({ row }) => (
          <span className="font-mono text-slate-600 text-sm">{row.original.codigo_obra}</span>
        ),
      },
      {
        accessorKey: "nombre",
        header: "Nombre",
        cell: ({ row }) => (
          <span className="font-medium text-slate-900">{row.original.nombre}</span>
        ),
      },
      {
        id: "descripcion",
        accessorFn: (o) => o.descripcion ?? "—",
        header: "Descripción",
        cell: ({ getValue }) => <span className="text-slate-500">{getValue<string>()}</span>,
        meta: { className: "hidden md:table-cell" },
      },
      {
        id: "estado",
        accessorFn: (o) => (o.activa ? "Activa" : "Inactiva"),
        header: "Estado",
        cell: ({ row }) => (
          <span
            className={`inline-flex rounded-full px-2.5 py-0.5 font-medium text-sm ${
              row.original.activa ? "bg-green-100 text-green-800" : "bg-slate-200 text-slate-600"
            }`}
          >
            {row.original.activa ? "Activa" : "Inactiva"}
          </span>
        ),
      },
      {
        id: "acciones",
        header: () => <span className="sr-only">Acciones</span>,
        enableSorting: false,
        enableGlobalFilter: false,
        // Contiene el enlace a la configuración de la obra y los botones de editar/eliminar:
        // la celda entera detiene la propagación para no disparar la edición al pulsar en ella.
        meta: { className: "text-right", stopRowClick: true },
        cell: ({ row }) => {
          const o = row.original;
          return (
            <div className="flex justify-end gap-1">
              <Button
                variant="ghost"
                size="icon"
                asChild
                aria-label={`Configuración de ${o.nombre}`}
              >
                <Link to="/obras/$obraId/configuracion" params={{ obraId: String(o.id_obra) }}>
                  <Settings2 className="h-5 w-5" aria-hidden="true" />
                </Link>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Editar ${o.nombre}`}
                onClick={() => setFormState({ mode: "editar", obra: o })}
              >
                <Pencil className="h-5 w-5" aria-hidden="true" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Eliminar ${o.nombre}`}
                onClick={() => setABorrar(o)}
              >
                <Trash2 className="h-5 w-5 text-red-600" aria-hidden="true" />
              </Button>
            </div>
          );
        },
      },
    ],
    [],
  );

  function cerrarForm() {
    setFormState(null);
  }

  async function onSubmitForm(values: ObraFormValues) {
    if (formState?.mode === "editar") {
      await editar.mutateAsync({ id: formState.obra.id_obra, input: toInput(values) });
      cerrarForm();
      return;
    }
    const creada = await crear.mutateAsync(toInput(values));
    cerrarForm();
    // Encadena el alta con la configuración de la obra para poblarla (#187, #238, #281).
    setObraCreada(creada);
  }

  async function confirmarBorrado() {
    if (!aBorrar) {
      return;
    }
    await borrar.mutateAsync(aBorrar.id_obra);
    setABorrar(null);
  }

  // El backend es el guardián real (RequireAdmin sobre la colección); la UI se
  // auto-oculta para no ofrecer una sección vetada, además del guard de ruta.
  if (me && !esAdmin) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
        <span>No tienes permiso para gestionar obras.</span>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col">
          <h1 className="font-bold text-2xl text-slate-900 tracking-tight">Gestión de obras</h1>
          <p className="text-base text-slate-500">
            Obras de la instancia, sus miembros y autorizaciones LOTO.
          </p>
        </div>
        <Button onClick={() => setFormState({ mode: "nueva" })}>
          <Plus className="h-5 w-5" aria-hidden="true" />
          Nueva obra
        </Button>
      </div>

      {isError ? (
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>No se pudieron cargar las obras. Inténtalo de nuevo.</span>
        </Alert>
      ) : null}

      {isPending ? (
        <div className="flex items-center justify-center gap-2 py-10 text-slate-500">
          <LoaderCircle className="h-6 w-6 animate-spin" aria-hidden="true" />
          Cargando obras…
        </div>
      ) : null}

      {!isPending && !isError && obras.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 border-dashed bg-white px-4 py-12 text-center text-slate-500">
          Aún no hay obras. Crea la primera con «Nueva obra».
        </div>
      ) : null}

      {!isPending && !isError && obras.length > 0 ? (
        <DataTable
          columns={columns}
          data={obras}
          searchPlaceholder="Buscar obras…"
          onRowClick={(obra) => setFormState({ mode: "editar", obra })}
        />
      ) : null}

      {/* Alta / edición */}
      <Modal
        open={formState !== null}
        onClose={cerrarForm}
        title={formState?.mode === "editar" ? "Editar obra" : "Nueva obra"}
      >
        {formState !== null ? (
          <ObraForm
            defaultValues={
              formState.mode === "editar"
                ? {
                    nombre: formState.obra.nombre,
                    descripcion: formState.obra.descripcion ?? "",
                    activa: formState.obra.activa,
                  }
                : { nombre: "", descripcion: "", activa: true }
            }
            submitLabel={formState.mode === "editar" ? "Guardar cambios" : "Crear obra"}
            pending={crear.isPending || editar.isPending}
            onSubmit={onSubmitForm}
            onCancel={cerrarForm}
          />
        ) : null}
      </Modal>

      {/* Confirmación de borrado */}
      <Modal open={aBorrar !== null} onClose={() => setABorrar(null)} title="Eliminar obra">
        <p className="text-base text-slate-600">
          ¿Seguro que quieres eliminar la obra <strong>{aBorrar?.nombre}</strong>? Esta acción la da
          de baja.
        </p>
        {borrar.isError ? (
          <Alert variant="destructive">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <span>No se pudo eliminar la obra. Inténtalo de nuevo.</span>
          </Alert>
        ) : null}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={() => setABorrar(null)} disabled={borrar.isPending}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={() => void confirmarBorrado()}
            disabled={borrar.isPending}
          >
            {borrar.isPending ? (
              <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
            ) : (
              <Trash2 className="h-5 w-5" aria-hidden="true" />
            )}
            Eliminar
          </Button>
        </div>
      </Modal>

      {/* Paso guiado tras crear: añadir miembros a la obra recién creada */}
      <Modal open={obraCreada !== null} onClose={() => setObraCreada(null)} title="Obra creada">
        <p className="text-base text-slate-600">
          Se creó <strong>{obraCreada?.nombre}</strong> ({obraCreada?.codigo_obra}). Añádele
          miembros para que puedan trabajar en ella.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={() => setObraCreada(null)}>
            Ahora no
          </Button>
          {obraCreada ? (
            <Button asChild>
              {/* #281 R7: el paso guiado lleva a la configuración de ESTA obra —donde está el
                  alta rápida de miembros con su autorización LOTO—, y no al directorio
                  global, que dejaba al ADMIN sin ninguna referencia a la obra que acababa
                  de crear. */}
              <Link
                to="/obras/$obraId/configuracion"
                params={{ obraId: String(obraCreada.id_obra) }}
              >
                Añadir miembros
                <ArrowRight className="h-5 w-5" aria-hidden="true" />
              </Link>
            </Button>
          ) : null}
        </div>
      </Modal>
    </div>
  );
}
